// API Key管理页面 - Tailwind CSS 现代简约风格
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Eye, EyeOff, Key, Shield, Calendar, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';  // 添加这一行
import ConfirmDialog from '../components/ConfirmDialog';  // 添加这一行
import LoadingOverlay from '../components/LoadingOverlay';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateKeyDialog from '../components/CreateKeyDialog';  // 添加
import KeySuccessDialog from '../components/KeySuccessDialog';

interface APIKey {
  id: string;
  api_key?: string;           // 改为可选
  masked_key?: string;        // 新增遮挡的key
  key_format?: string;        // 新增格式信息
  key_environment?: string;   // 新增环境信息
  key_strength?: string;      // 新增安全强度
  key_name: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  daily_call_limit: number | null;
  monthly_call_limit: number | null;
  daily_cost_limit: number | null;
  monthly_cost_limit: number | null;
  allowed_models: string[] | null;
  deleted_at?: string | null;
}

const APIKeys: React.FC = () => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  // 显示Toast通知
  const showToast = (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => {
    setToast({
      isVisible: true,
      type,
      title,
      message: message || ''
    });
  };

  // 关闭Toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // 显示删除确认对话框
  const showDeleteConfirm = (keyId: string) => {
    const keyToDelete = keys.find(key => key.id === keyId);
    setConfirmDialog({
      isOpen: true,
      keyId,
      keyName: keyToDelete?.key_name || 'Unknown Key'
    });
  };

  // 关闭确认对话框
  const closeDeleteConfirm = () => {
    setConfirmDialog({
      isOpen: false,
      keyId: '',
      keyName: ''
    });
  };

  // 显示创建Key对话框 - 新增
  const showCreateDialog = () => {
    setCreateDialog({ isOpen: true });
  };

  // 关闭创建对话框 - 新增
  const closeCreateDialog = () => {
    setCreateDialog({ isOpen: false });
  };

  // 关闭成功对话框 - 新增
  const closeSuccessDialog = () => {
    setSuccessDialog({
      isOpen: false,
      apiKey: '',
      keyName: ''
    });
  };

  // Toast状态
  const [toast, setToast] = useState({
    isVisible: false,
    type: 'success' as 'success' | 'error' | 'warning' | 'info',
    title: '',
    message: ''
  });

  // 删除相关状态
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    keyId: '',
    keyName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // 创建相关状态 - 新增
  const [createDialog, setCreateDialog] = useState({
    isOpen: false
  });
  const [isCreating, setIsCreating] = useState(false);
  const [successDialog, setSuccessDialog] = useState({
    isOpen: false,
    apiKey: '',
    keyName: ''
  });

  const maskApiKey = (apiKey: string) => {
    if (!apiKey || apiKey.length <= 8) return apiKey;
    return apiKey.substring(0, 8) + '****' + apiKey.substring(apiKey.length - 4);
  };

  const copyApiKey = async (apiKey: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (err) {
      alert('Failed to copy API Key');
    }
  };

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await api.get('/api/keys');
      setKeys(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const createAPIKey = async (keyName: string) => {
    setIsCreating(true);

    // 先关闭创建对话框
    closeCreateDialog();

    try {
      const response = await api.post('/api/keys', {
        key_name: keyName,
      });

      // 关闭加载状态
      setIsCreating(false);

      // 稍等片刻后显示成功对话框
      setTimeout(() => {
        setSuccessDialog({
          isOpen: true,
          apiKey: response.data.api_key,
          keyName: keyName
        });
      }, 100);

      // 刷新API Keys列表
      fetchAPIKeys();

      // 清除错误状态
      setError('');

    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to create API key';

      // 关闭加载状态
      setIsCreating(false);

      // 延迟显示错误Toast
      setTimeout(() => {
        showToast('error', 'Creation Failed', errorMessage);
      }, 100);

      setError(errorMessage);
    }
  };

  const deleteAPIKey = async (keyId: string) => {
    setIsDeleting(true);

    // 先关闭确认对话框
    closeDeleteConfirm();

    try {
      const response = await api.delete(`/api/keys/${keyId}`);

      // 刷新API Keys列表
      await fetchAPIKeys();

      // 清除错误状态
      setError('');

      // 先关闭遮罩
      setIsDeleting(false);

      // 然后显示成功Toast（延迟一点确保遮罩先关闭）
      setTimeout(() => {
        showToast('success', t('apiKeys.keyDeletedTitle'), `"${confirmDialog.keyName}" ${t('apiKeys.keyDeletedMessage')}`);
      }, 100);

    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || t('apiKeys.deleteFailedMessage');

      // 关闭遮罩
      setIsDeleting(false);

      // 延迟显示错误Toast
      setTimeout(() => {
        showToast('error', t('apiKeys.deleteFailedTitle'), errorMessage);
      }, 100);

      setError(errorMessage);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('apiKeys.never');
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <LoadingSpinner size="medium" text={t('apiKeys.loadingKeys')} fullPage={true} />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 页面标题 */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Key className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('apiKeys.title')}</h1>
                <p className="text-xs text-gray-600 mt-1">
                  {t('apiKeys.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={showCreateDialog}
              className="bg-gray-900 text-white font-medium py-2 px-3 rounded-lg hover:bg-gray-800 transition duration-200 flex items-center space-x-2 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>{t('apiKeys.createNewKey')}</span>
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
              <span>{error}</span>
            </div>
          )}

          {/* API Keys列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {keys.map((key) => (
              <div key={key.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-2">{key.key_name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          key.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {key.is_active ? t('apiKeys.active') : t('apiKeys.inactive')}
                        </span>
                        {key.key_name === 'Default Key' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('apiKeys.cannotBeDeleted')}
                          </span>
                        )}
                      </div>
                    </div>
                    {key.key_name !== 'Default Key' && (
                      <button
                        onClick={() => showDeleteConfirm(key.id)}
                        className="text-gray-400 hover:text-red-500 transition duration-200"
                        title={t('apiKeys.deleteKey')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('apiKeys.apiKeyLabel')}</label>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 bg-gray-50 px-2 py-1.5 rounded-lg text-xs font-mono text-gray-700 border">
                          {key.masked_key}
                        </code>
                        <div className="relative">
                          <button
                            onClick={() => copyApiKey(key.api_key || '', key.id)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition duration-200"
                            title={copiedKeyId === key.id ? t('apiKeys.copied') : t('apiKeys.copyApiKey')}
                          >
                            {copiedKeyId === key.id ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                          {copiedKeyId === key.id && (
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg">
                              {t('apiKeys.copied')}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-900"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-xs">
                        <Calendar className="h-3 w-3 text-gray-400 mr-2" />
                        <div>
                          <span className="text-gray-500">{t('apiKeys.created')}: </span>
                          <span className="text-gray-900 font-medium">{formatDate(key.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center text-xs">
                        <Eye className="h-3 w-3 text-gray-400 mr-2" />
                        <div>
                          <span className="text-gray-500">{t('apiKeys.lastUsed')}: </span>
                          <span className="text-gray-900 font-medium">{formatDate(key.last_used_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {keys.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Key className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-base font-medium text-gray-900 mb-2">{t('apiKeys.noKeysFound')}</h3>
                <p className="text-xs text-gray-600 mb-4 text-center max-w-md">
                  {t('apiKeys.createFirstKey')}
                </p>
                <button
                  onClick={showCreateDialog}
                  className="bg-blue-600 text-white font-medium py-2 px-3 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center space-x-2 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>{t('apiKeys.createYourFirstKey')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast通知组件 */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={closeToast}
        duration={4000}
      />

      {/* 确认删除对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={t('apiKeys.deleteKeyTitle')}
        message={`${t('apiKeys.deleteConfirmMessage')} "${confirmDialog.keyName}"?\n\n${t('apiKeys.deleteWarning')}`}
        confirmText={t('apiKeys.deleteKeyButton')}
        cancelText={t('apiKeys.cancel')}
        onConfirm={() => deleteAPIKey(confirmDialog.keyId)}
        onCancel={closeDeleteConfirm}
        type="danger"
      />

      {/* 加载遮罩 */}
      <LoadingOverlay
        isVisible={isDeleting}
        message={t('apiKeys.deletingKey')}
      />

      {/* 创建API Key对话框 */}
      <CreateKeyDialog
        isOpen={createDialog.isOpen}
        onConfirm={createAPIKey}
        onCancel={closeCreateDialog}
        isLoading={isCreating}
      />

      {/* 创建成功对话框 */}
      <KeySuccessDialog
        isOpen={successDialog.isOpen}
        apiKey={successDialog.apiKey}
        keyName={successDialog.keyName}
        onClose={closeSuccessDialog}
      />

      {/* 创建加载遮罩 */}
      <LoadingOverlay
        isVisible={isCreating}
        message={t('apiKeys.creatingKey')}
      />
    </div>
  );
};

export default APIKeys;